import { apiRequest, ApiResponse } from '../request'

export type OnboardingProfile = 'CLINIC' | 'INDIVIDUAL_DOCTOR'

/** Uma linha da lista de custos do consultório. Valor sempre em centavos. */
export interface OnboardingCost {
    description: string
    amount: number
}

export interface OnboardingSpecialty {
    specialtyId?: number
    name: string
    /** Se o médico atende consultas nesta especialidade. */
    consults?: boolean
    /** Valor cobrado pela consulta, em centavos. */
    consultationPrice?: number
    /** Teto de desconto em porcentagem. */
    maxDiscount?: number
}

export interface OnboardingSurgery {
    name: string
    /** Código TUSS confirmado pelo médico; sem ele o item nasce com nome livre. */
    tussCode?: string
}

export interface OnboardingAddress {
    label?: string
    street?: string
    number?: string
    complement?: string
    neighborhood?: string
    city?: string
    /** Código IBGE do município — preenchido pela consulta de CEP, exigido na NFS-e. */
    cityCode?: string
    state?: string
    zipCode?: string
    country?: string
}

export interface OnboardingDoctor {
    name: string
    registrationNumber?: string
    councilName?: string
    document?: string
    phoneNumber?: string
    address?: OnboardingAddress
}

export interface SubmitOnboardingDto {
    profile: OnboardingProfile
    doctor?: OnboardingDoctor
    specialties?: OnboardingSpecialty[]
    costs?: OnboardingCost[]
    /**
     * Consultas num mês típico. Divide o custo mensal para chegar ao custo de UMA
     * consulta — sem ela, o custo do mês inteiro viraria o custo de cada atendimento.
     */
    monthlyConsultations?: number
    isSurgeon?: boolean
    surgeries?: OnboardingSurgery[]
}

export interface OnboardingStatus {
    /** Assistente pendente numa conta de saúde que não seja de intermediação. */
    required: boolean
    segment: string | null
    secondarySegment: string | null
    profile: OnboardingProfile | null
    completedAt: string | null
    /** Rascunho do "Continuar depois". */
    answers: Record<string, unknown> | null
}

export interface OnboardingResult {
    professionalId: number | null
    specialtyIds: number[]
    consultationCatalogIds: number[]
    surgeryCatalogIds: number[]
    examsQueued: boolean
}

export interface TussSuggestion {
    tussCode: string
    term: string
}

export async function getOnboardingStatus(): Promise<ApiResponse<OnboardingStatus>> {
    return apiRequest<OnboardingStatus>('onboarding/status', {}, 'get')
}

export async function getSurgerySuggestions(
    q: string
): Promise<ApiResponse<TussSuggestion[]>> {
    return apiRequest<TussSuggestion[]>('onboarding/surgery-suggestions', { params: { q } }, 'get')
}

/** "Continuar depois": guarda o preenchido até aqui, sem concluir o assistente. */
export async function saveOnboardingDraft(data: {
    profile?: OnboardingProfile
    step?: number
    answers: Record<string, unknown>
}): Promise<ApiResponse<unknown>> {
    return apiRequest('onboarding/draft', { data }, 'put')
}

export async function submitOnboarding(
    data: SubmitOnboardingDto
): Promise<ApiResponse<{ result: OnboardingResult }>> {
    return apiRequest<{ result: OnboardingResult }>('onboarding/submit', { data }, 'post')
}
